/**
 * Featured products on the PDP — swaps the section collection fallback for
 * Shopify's related products (Search & Discovery picks first, automatic ones
 * after), fetched by re-rendering the section through /recommendations/products.
 * With no recommendations the fallback stays, or the row stays hidden.
 */
(() => {
  const TAG = 'featured-products-recommendations';
  const SECTION_SELECTOR = 'section.featured-products';
  const SLIDE_SELECTOR = '.featured-products__slide';

  if (customElements.get(TAG)) return;

  /**
   * Scripts inside parsed HTML do not run once inserted. With no collection
   * fallback the page never rendered a card, so card scripts (product-card,
   * quick-add) were never loaded. Load any the incoming row needs.
   */
  const loadMissingScripts = (root) => {
    // The incoming row is already in the page, so its own inert tags do not count
    const loaded = new Set(
      [...document.querySelectorAll('script[src]')]
        .filter((script) => !root.contains(script))
        .map((script) => new URL(script.getAttribute('src'), window.location.href).href)
    );

    for (const script of root.querySelectorAll('script[src]')) {
      // Asset URLs are protocol-relative; resolve against this page to compare
      const src = new URL(script.getAttribute('src'), window.location.href).href;

      if (loaded.has(src)) continue;

      const next = document.createElement('script');

      next.src = src;
      next.defer = true;
      if (script.type) next.type = script.type;

      document.head.appendChild(next);
      loaded.add(src);
    }
  };

  class FeaturedProductsRecommendations extends HTMLElement {
    #abort = null;

    connectedCallback() {
      if (!this.dataset.url) return;

      this.#load();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
    }

    async #load() {
      this.#abort?.abort();
      this.#abort = new AbortController();

      try {
        const response = await fetch(this.dataset.url, { signal: this.#abort.signal });

        if (!response.ok) return;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector(`${TAG} ${SECTION_SELECTOR}`);

        // An empty recommendations render keeps whatever the page already shows
        if (!next || !next.querySelector(SLIDE_SELECTOR)) return;

        const incoming = document.importNode(next, true);
        const current = this.querySelector(SECTION_SELECTOR);

        window.themeCarousel?.destroyAll?.(this);

        if (current) {
          current.replaceWith(incoming);
        } else {
          this.append(incoming);
        }

        this.hidden = false;
        loadMissingScripts(incoming);

        // carousel.js mounts on load; a row that arrives later needs a nudge
        window.themeCarousel?.evaluateAll?.(this);
      } catch (error) {
        // A failed or aborted request leaves the fallback in place
      }
    }
  }

  customElements.define(TAG, FeaturedProductsRecommendations);
})();
